import React from "react";
import { Link } from "react-router-dom";
import { useCurrency } from "../context/CurrencyContext";
import { MapPin, Clock, Users, BadgeCheck, Star, Crown } from "lucide-react";
import FavouriteButton from "./FavouriteButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function photoSrc(p) {
  if (!p) return "";
  if (p.startsWith("http")) return p;
  return `${API}/files/${p}`;
}

const typeLabels = {
  hotel: "Hotel",
  guest_house: "Guest House",
  lodge: "Lodge",
  apartment: "Apartment",
};

export default function PropertyCard({ property, featured = false }) {
  const { format } = useCurrency();
  const hero = property.photos?.[0];
  return (
    <Link
      to={`/property/${property.id}`}
      data-testid={`property-card-${property.id}`}
      className={`group relative flex flex-col overflow-hidden rounded-3xl bg-white card-shadow transition ${featured ? "md:col-span-8" : "md:col-span-4"}`}
    >
      <div className={`relative w-full overflow-hidden ${featured ? "aspect-[16/10]" : "aspect-[4/3]"}`}>
        {hero ? (
          <img
            src={photoSrc(hero)}
            alt={property.title}
            className="h-full w-full object-cover transition duration-700 group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div className="h-full w-full bg-gradient-to-br from-copper/15 to-forest/15" />
        )}
        <div className="absolute left-4 top-4 chip !bg-white/90 !text-forest">
          {typeLabels[property.type] || property.type}
        </div>
        <div className="absolute right-4 top-4 flex items-center gap-2">
          <div onClickCapture={(e) => e.preventDefault()}>
            <FavouriteButton propertyId={property.id} initial={property.is_favourite} />
          </div>
          <div className="chip !bg-copper !text-white">
            from {format(property.price_per_hour_zmw)}/hr
          </div>
        </div>
        {property.host_is_superhost && (
          <div
            data-testid={`superhost-badge-${property.id}`}
            className="absolute left-4 bottom-4 flex items-center gap-1 rounded-full bg-forest px-3 py-1 text-xs font-semibold text-white shadow-lg"
            title={`Superhost · ${property.host_avg_rating?.toFixed?.(1)} ★ across ${property.host_review_count} reviews`}
          >
            <Crown className="h-3.5 w-3.5" /> Superhost
          </div>
        )}
      </div>
      <div className="flex flex-1 flex-col gap-3 p-5">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-display text-lg font-bold leading-tight text-slate-900 line-clamp-1">
            {property.title}
          </h3>
          {property.host_verified && (
            <span
              data-testid={`verified-host-badge-${property.id}`}
              title={`Verified host${property.host_name ? " · " + property.host_name : ""}`}
              className="mt-0.5 shrink-0"
            >
              <BadgeCheck className="h-5 w-5 fill-copper text-white" />
            </span>
          )}
        </div>
        <div className="flex items-center gap-1 text-sm text-slate-500">
          <MapPin className="h-4 w-4" /> {property.city}
        </div>
        {property.review_count > 0 && (
          <div
            data-testid={`property-rating-${property.id}`}
            className="flex items-center gap-1 text-xs font-semibold text-slate-700"
          >
            <Star className="h-3.5 w-3.5 fill-copper text-copper" />
            {property.avg_rating?.toFixed?.(1) ?? property.avg_rating}
            <span className="font-normal text-slate-500">({property.review_count})</span>
          </div>
        )}
        <div className="flex items-center gap-4 text-xs text-slate-600">
          <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> min {property.min_hours}h</span>
          <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {property.max_guests} guests</span>
        </div>
        <div className="mt-auto flex items-end justify-between pt-2">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500">per night</div>
            <div className="font-display text-xl font-bold text-slate-900">{format(property.price_per_night_zmw)}</div>
          </div>
          <div className="text-xs text-forest font-semibold">Book →</div>
        </div>
      </div>
    </Link>
  );
}

export { photoSrc };
